// SemanticUI-free pre-@plone/components
import React from 'react';

import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { default as UniversalLink } from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';

import { Container } from '@plone/components';
import { Button, Segment, List } from 'semantic-ui-react';
import config from '@plone/volto/registry';
import arrowRight from '../../theme/icons/arrow-right.svg';

import facebookSVG from '../../theme/icons/facebook_square.svg';
import instagramSVG from '../../theme/icons/instagram.svg';
import linkedinSVG from '../../theme/icons/linkedin.svg';
import twitterSVG from '../../theme/icons/twitter.svg';
import youtubeSVG from '../../theme/icons/youtube.svg';
import cx from 'classnames';

import fbcImage from '../../theme/images/fbc.png';
import basqueImage from '../../theme/images/basque_audiovisual.png';
import { CONTACTS } from '../../constants';
import { useGoogleAnalytics } from 'volto-google-analytics';

const messages = defineMessages({
  copyright: {
    id: 'Copyright',
    defaultMessage: 'Copyright',
  },
  contact: {
    id: 'Contact',
    defaultMessage: 'Contact',
  },
  basqueAudiovisual: {
    id: 'Basque Audiovisual',
    defaultMessage: 'Basque Audiovisual',
  },
  fbc: {
    id: 'FBC',
    defaultMessage: 'FBC',
  },
  footeremail: {
    id: 'footeremail',
    defaultMessage: 'fbc@basqueaudiovisual.eus',
  },
  footerdirection: {
    id: 'footerdirection',
    defaultMessage:
      'Tabakalera, 3ª planta. Plaza de las cigarreras, 1.{br}20012 Donostia / San Sebastián',
  },
});

/**
 * Component to display the footer.
 * @function Footer
 * @param {Object} intl Intl object
 * @returns {string} Markup of the component
 */
const Footer = ({ intl }) => {
  useGoogleAnalytics();
  const { settings } = config;
  const { lang } = useSelector(
    (state) => ({
      lang: state.intl.locale,
      siteActions: state.actions?.actions?.site_actions,
    }),
    shallowEqual,
  );

  return (
    <footer id="footer">
      <Container layout className="footer">
        <div className="footer-left">
          <div className="footer-logo">
            <img
              src={basqueImage}
              className="basque-footer-logo"
              alt={intl.formatMessage(messages.basqueAudiovisual)}
              title={intl.formatMessage(messages.basqueAudiovisual)}
            />
            <img
              src={fbcImage}
              className="fbc-footer-logo"
              alt={intl.formatMessage(messages.fbc)}
              title={intl.formatMessage(messages.fbc)}
            />
          </div>
          <div className="footer-info">
            <p className="fbc-description size-small">
              <FormattedMessage
                id="+34 943 595 862"
                defaultMessage="+34 943 595 862"
              />
              <br />
              <a href={`mailto:${intl.formatMessage(messages.footeremail)}`}>
                {intl.formatMessage(messages.footeremail)}
              </a>
            </p>
            <p className="fbc-description size-small">
              {intl.formatMessage(messages.footerdirection, { br: <br /> })}
            </p>
            <p className="fbc-description size-small">
              <a
                className="google-maps-link"
                href="https://maps.app.goo.gl/Xgw6L3jjysqa5rP6A"
              >
                <FormattedMessage
                  id="See in google maps"
                  defaultMessage="See in google maps"
                />
              </a>
            </p>
          </div>
        </div>
        <div className="footer-right">
          <UniversalLink href={`/${lang}/${CONTACTS[lang]}`} as="button">
            <Button className={cx('transparent contact-footer-button')}>
              {intl.formatMessage(messages.contact)}{' '}
              <Icon
                className="arrow svg-inline--fa"
                name={arrowRight}
                title="arrow"
              />
            </Button>
          </UniversalLink>
          <div className="discreet footer-links-container">
            <ul>
              {/* wrap in div for a11y reasons: listitem role cannot be on the <a> element directly */}
              <li>
                <UniversalLink
                  href={
                    settings.isMultilingual
                      ? `/${lang}/terms-of-use`
                      : '/terms-of-use'
                  }
                >
                  <FormattedMessage
                    id="Terms of use"
                    defaultMessage="Terms of use"
                  />
                </UniversalLink>
              </li>
              /
              <li>
                <UniversalLink
                  href={
                    settings.isMultilingual ? `/${lang}/privacy` : '/privacy'
                  }
                >
                  <FormattedMessage
                    id="Privacy policy"
                    defaultMessage="Privacy policy"
                  />
                </UniversalLink>
              </li>
              /
              <li>
                <UniversalLink
                  className="item"
                  href={
                    settings.isMultilingual
                      ? `/${lang}/cookies-policy`
                      : '/cookies-policy'
                  }
                >
                  <FormattedMessage
                    id="Cookies policy"
                    defaultMessage="Cookies policy"
                  />
                </UniversalLink>
              </li>
              {/*
              /
              <li>
                <UniversalLink
                  className="item"
                  href={
                    settings.isMultilingual
                      ? `/${lang}/irisgarritasuna`
                      : '/irisgarritasuna'
                  }
                >
                  <FormattedMessage
                    id="Accessibility"
                    defaultMessage="Accessibility"
                  />
                </UniversalLink>
              </li> */}
            </ul>
          </div>
          <Segment basic className="footer-social-container">
            <List horizontal>
              <List.Item>
                <UniversalLink href="https://www.instagram.com">
                  <Icon className="social-icon" name={instagramSVG} />{' '}
                </UniversalLink>
              </List.Item>
              <List.Item>
                <UniversalLink href="https://www.twitter.com">
                  <Icon className="social-icon" name={twitterSVG} />{' '}
                </UniversalLink>
              </List.Item>
              <List.Item>
                <UniversalLink href="https://www.facebook.com">
                  <Icon className="social-icon" name={facebookSVG} />{' '}
                </UniversalLink>
              </List.Item>
              <List.Item>
                <UniversalLink href="https://www.youtube.com">
                  <Icon className="social-icon" name={youtubeSVG} />{' '}
                </UniversalLink>
              </List.Item>
              <List.Item>
                <UniversalLink href="https://www.linkedin.com">
                  <Icon className="social-icon" name={linkedinSVG} />{' '}
                </UniversalLink>
              </List.Item>
            </List>
          </Segment>
        </div>
      </Container>
    </footer>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Footer.propTypes = {
  /**
   * i18n object
   */
};

export default injectIntl(Footer);
